'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import type { FilterOption } from '@/lib/api/types';

/**
 * Filter UI is entirely driven by the category's filter option list from the
 * API (spec §30 — never hardcode filters per vertical in the frontend).
 */
export function CategoryFilters({
  categorySlug,
  options,
}: {
  categorySlug: string;
  options: FilterOption[];
}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  function updateParam(key: string, value: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (value === null || value === '') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    router.push(`/categorias/${categorySlug}?${params.toString()}`);
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <aside className="flex w-full flex-col gap-6 rounded-lg border border-gray-border bg-white p-4">
      <h2 className="hidden text-sm font-semibold text-navy sm:block">Filtros</h2>

      {options.map((option) => {
        if (option.data_type === 'boolean') {
          const paramKey = `specs[${option.key}]`;
          const checked = searchParams.get(paramKey) === '1';

          return (
            <label key={option.key} className="flex items-center gap-2 text-sm text-navy">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => updateParam(paramKey, e.target.checked ? '1' : null)}
                className="h-4 w-4 accent-cobalt"
              />
              {option.label}
            </label>
          );
        }

        if (option.data_type === 'number') {
          const minKey = `specs[${option.key}][min]`;
          const maxKey = `specs[${option.key}][max]`;

          return (
            <div key={option.key} className="flex flex-col gap-1">
              <span className="text-sm font-medium text-navy">
                {option.label}
                {option.unit ? ` (${option.unit})` : ''}
              </span>
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Mín"
                  defaultValue={searchParams.get(minKey) ?? ''}
                  onBlur={(e) => updateParam(minKey, e.target.value)}
                  className="w-full rounded border border-gray-border px-2 py-1 text-sm"
                />
                <input
                  type="number"
                  placeholder="Máx"
                  defaultValue={searchParams.get(maxKey) ?? ''}
                  onBlur={(e) => updateParam(maxKey, e.target.value)}
                  className="w-full rounded border border-gray-border px-2 py-1 text-sm"
                />
              </div>
            </div>
          );
        }

        return null;
      })}
    </aside>
  );
}
